import type { BaseSaisies, EnregistrementBrut, NomMagasin, TransactionMagasins } from './idb';
import { creerStockage, type StockageFile } from './stockage';

/**
 * Repli quand le navigateur refuse IndexedDB (ouverture refusée, quota
 * dépassé, autre version de l'application) : mêmes magasins et mêmes règles,
 * en mémoire de l'onglet seulement. Les saisies y survivent tant que l'écran
 * reste ouvert ; le bandeau prévient l'utilisateur.
 *
 * Import croisé avec `stockage.ts` : chacun n'utilise l'autre qu'à l'appel,
 * jamais au chargement du module.
 */

export interface InstantaneSaisies {
  envois: EnregistrementBrut[];
  brouillons: EnregistrementBrut[];
  meta: Record<string, unknown>;
}

type Magasin = Map<string, EnregistrementBrut>;
interface Etat {
  envois: Magasin;
  brouillons: Magasin;
  meta: Map<string, unknown>;
}

/** Copie comme IndexedDB : l'appelant ne modifie jamais ce qui est stocké. */
function copier<T>(valeur: T): T {
  if (valeur === undefined) return valeur;
  return typeof structuredClone === 'function' ? structuredClone(valeur) : (JSON.parse(JSON.stringify(valeur)) as T);
}

const cleTexte = (cle: IDBValidKey | readonly unknown[]) => JSON.stringify(cle);

function magasin(lignes: readonly EnregistrementBrut[] = []): Magasin {
  return new Map(lignes.map((ligne) => [cleTexte(ligne.cle), copier(ligne)]));
}

function transaction(etat: Etat, mode: 'readonly' | 'readwrite'): TransactionMagasins {
  const ecriture = () => {
    if (mode !== 'readwrite') throw new DOMException('Transaction en lecture seule', 'ReadOnlyError');
  };
  return {
    async lire(nom: NomMagasin, cle) {
      return copier(etat[nom].get(cleTexte(cle))?.valeur);
    },
    async ecrire(nom, valeur) {
      ecriture();
      const cle = [valeur.utilisateurId, valeur.contactId];
      etat[nom].set(cleTexte(cle), { cle, valeur: copier(valeur) });
    },
    async effacer(nom, cle) {
      ecriture();
      etat[nom].delete(cleTexte(cle));
    },
    async tous(nom) {
      return [...etat[nom].entries()]
        .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
        .map(([, ligne]) => copier(ligne));
    },
    async lireMeta(cle) {
      return copier(etat.meta.get(cle));
    },
    async ecrireMeta(cle, valeur) {
      ecriture();
      etat.meta.set(cle, copier(valeur));
    },
  };
}

/**
 * Base en mémoire : les transactions passent une à une ; une transaction
 * d'écriture travaille sur une copie, adoptée seulement si tout le travail
 * aboutit (même atomicité qu'IndexedDB).
 */
export function baseMemoire(instantane?: InstantaneSaisies): BaseSaisies {
  let etat: Etat = {
    envois: magasin(instantane?.envois),
    brouillons: magasin(instantane?.brouillons),
    meta: new Map(Object.entries(copier(instantane?.meta ?? {}))),
  };
  let file: Promise<unknown> = Promise.resolve();
  let fermee = false;

  return {
    genre: 'memoire',
    executer<T>(mode: 'readonly' | 'readwrite', travail: (tx: TransactionMagasins) => Promise<T>): Promise<T> {
      const tour = file.then(async () => {
        if (fermee) throw new Error('Base locale fermée');
        const cible: Etat =
          mode === 'readwrite'
            ? { envois: new Map(etat.envois), brouillons: new Map(etat.brouillons), meta: new Map(etat.meta) }
            : etat;
        const resultat = await travail(transaction(cible, mode));
        if (mode === 'readwrite') etat = cible;
        return resultat;
      });
      file = tour.catch(() => undefined);
      return tour;
    },
    fermer() {
      fermee = true;
    },
  };
}

export function stockageMemoire(): StockageFile {
  return creerStockage(baseMemoire());
}
